import React, { useState } from "react";
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SocialAuthButtons from "../components/SocialAuthButtons";
import "./Login.css";
import FondoDeGranos, { FONDO_AUTH_ANCHO, FONDO_AUTH_ANGOSTO, RESPALDO_AUTH, usePantallaAncha } from '../components/business/FondoDeGranos';

export default function Login() {
  const pantallaAncha = usePantallaAncha();
  const navigate = useNavigate();
  const { login, currentUser, userData, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Redirect authenticated users
  //
  // `userData` entra en la condición, no sólo en el cuerpo: sin eso, en el
  // instante en que la sesión existe pero los datos del negocio todavía no
  // llegaron, el `else` mandaba al onboarding a alguien que ya lo había
  // completado. Es la segunda mitad del parpadeo que se veía al iniciar sesión;
  // la otra está en AuthContext.
  React.useEffect(() => {
    if (loading || !currentUser || !userData) return;

    console.log('🔄 Login: sesión resuelta, redirigiendo...');
    navigate(userData.onboardingCompleted ? '/admin/dashboard' : '/admin/onboarding', {
      replace: true,
    });
  }, [currentUser, userData, loading, navigate]);

  const validateForm = () => {
    const newErrors = {};

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = "El email es requerido";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Ingresá un email válido";
    }

    // Validar contraseña
    if (!formData.password) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Intentar iniciar sesión con Firebase
      await login(formData.email, formData.password);
      
      // Si el login es exitoso, redirigir al dashboard
      navigate("/admin/dashboard");
    } catch (error) {
      setIsLoading(false);
      
      // Manejar errores de Firebase
      let errorMessage = "Error al iniciar sesión";
      
      switch (error.code) {
        case "auth/user-not-found":
          errorMessage = "No existe una cuenta con este email";
          break;
        case "auth/wrong-password":
          errorMessage = "Contraseña incorrecta";
          break;
        case "auth/invalid-email":
          errorMessage = "Email inválido";
          break;
        case "auth/user-disabled":
          errorMessage = "Esta cuenta ha sido deshabilitada";
          break;
        case "auth/too-many-requests":
          errorMessage = "Demasiados intentos fallidos. Intentá más tarde";
          break;
        default:
    
      }
      
      setErrors({ general: errorMessage });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleDemoLogin = () => {
    setFormData({
      email: "demo@aturno.com",
      password: "demo123"
    });
  };

  /* El mismo fondo que la landing dos, y el mismo componente. Antes estas tres
     pantallas tenían su propio degradado más tres esferas borroseadas que no
     existían en ningún otro lado de la app, así que pasar de la landing a
     "Ingresar" cambiaba de fondo sin motivo.

     `FondoDeGranos` va adentro de `.auth-container` —que ya es `position:
     relative` y `overflow: hidden`— y la tarjeta queda por encima gracias al
     z-index que ya tenía `.auth-content`. */
  return (
    <div className="auth-container">
      <FondoDeGranos
        posicion={pantallaAncha ? FONDO_AUTH_ANCHO : FONDO_AUTH_ANGOSTO}
        respaldo={RESPALDO_AUTH}
      />

      <div className="auth-content">
        <div className="auth-card">
          <div className="auth-header">
            <Link to="/" className="logo-link">
              <img 
                src="/logo-aturno.png" 
                alt="Aturno Logo" 
                className="auth-logo"
              />
            </Link>
            <h2>Iniciá sesión en tu cuenta</h2>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {errors.general && (
              <div className="error-banner">
                {errors.general}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tu@email.com"
                className={errors.email ? "error" : ""}
                autoComplete="email"
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password">Contraseña</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={errors.password ? "error" : ""}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password}</span>
              )}
            </div>

            <div className="form-actions">
              <Link to="/auth/forgot-password" className="forgot-link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              className="btn-primary auth-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Iniciando sesión...
                </>
              ) : (
                "Iniciar sesión"
              )}
            </button>

          </form>

          <SocialAuthButtons 
            onSuccess={(result) => {
              console.log('Login social exitoso:', result);
              navigate('/admin/dashboard');
            }}
            onError={(error) => {
              console.error('Error en login social:', error);
              setErrors({ general: error });
            }}
          />

          <div className="auth-footer">
            <p>
              ¿No tenés cuenta?{" "}
              <Link to="/auth/register" className="register-link">
                Registrate gratis
              </Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
} 